import { RotateCcw, AlertTriangle, ArrowRight } from "lucide-react";
import type { RuleResponse } from "../../gen/models";

interface RuleRevertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  targetRule: RuleResponse | null;
  activeRule: RuleResponse | null;
  nextVersionNumber: number;
  loading: boolean;
}

export function RuleRevertModal({
  isOpen,
  onClose,
  onConfirm,
  targetRule,
  activeRule,
  nextVersionNumber,
  loading,
}: RuleRevertModalProps) {
  if (!isOpen || !targetRule) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 dark:bg-black/70 backdrop-blur-xs">
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 shadow-xl max-w-xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/80 shrink-0">
            <RotateCcw className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
              Make Version {targetRule.version} Current
            </h3>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
              Revert this operational rule to the configuration from Version {targetRule.version}.
            </p>
          </div>
        </div>

        {/* Informational Explanation Box */}
        <div className="rounded-lg border border-amber-200 dark:border-amber-800/80 bg-amber-50/70 dark:bg-amber-950/40 p-4 text-xs text-amber-900 dark:text-amber-200 space-y-2">
          <div className="flex items-center gap-1.5 font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>How Rule Reversion Works</span>
          </div>
          <p className="leading-relaxed">
            Rules in Silvestre are strictly immutable. Reverting will <strong>not</strong> overwrite or erase past versions. Instead:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-[11px] leading-relaxed">
            <li>
              A new active version (<strong>v{nextVersionNumber}</strong>) will be created with the exact instruction text, priority, and scope from <strong>v{targetRule.version}</strong>.
            </li>
            {activeRule && (
              <li>
                The currently active version (<strong>v{activeRule.version}</strong>) will be marked as superseded.
              </li>
            )}
            <li>
              The new version will be permanently tagged in lineage history as a revert of <strong>v{targetRule.version}</strong>.
            </li>
          </ul>
        </div>

        {/* Content Preview Being Restored */}
        <div className="space-y-3 border-t border-stone-100 dark:border-stone-800 pt-3">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
            Configuration Restored from Version {targetRule.version}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div className="p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/60 dark:bg-stone-950/60">
              <span className="block text-[10px] text-stone-400 dark:text-stone-500 font-semibold uppercase">Scope</span>
              <span className="font-medium text-stone-800 dark:text-stone-200 capitalize">
                {targetRule.agent_type || "Universal (All)"}
              </span>
            </div>
            <div className="p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/60 dark:bg-stone-950/60">
              <span className="block text-[10px] text-stone-400 dark:text-stone-500 font-semibold uppercase">Category</span>
              <span className="font-medium text-stone-800 dark:text-stone-200 capitalize">
                {targetRule.category}
              </span>
            </div>
            <div className="p-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/60 dark:bg-stone-950/60 col-span-2 sm:col-span-1">
              <span className="block text-[10px] text-stone-400 dark:text-stone-500 font-semibold uppercase">Priority</span>
              <span className="font-mono font-bold text-stone-800 dark:text-stone-200">
                {targetRule.priority}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="block text-[11px] font-semibold text-stone-600 dark:text-stone-400">
              Instruction Content
            </span>
            <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/80 dark:bg-stone-950/80 p-3 font-mono text-[11px] text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap max-h-36 overflow-y-auto">
              {targetRule.instruction}
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-stone-100 dark:border-stone-800">
          <div className="flex items-center gap-1.5 text-xs text-stone-400 dark:text-stone-500">
            <span>v{targetRule.version}</span>
            <ArrowRight className="w-3 h-3" />
            <span className="font-bold text-stone-700 dark:text-stone-300">v{nextVersionNumber} (Active)</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-3.5 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-xs font-medium text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 transition shadow-2xs disabled:opacity-50 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>{loading ? "Reverting..." : `Revert & Activate v${nextVersionNumber}`}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
